import {
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  ListSubheader,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import { getEnabledEvacZoneStatuses } from 'features/evacZones/utils';
import { EVAC_ZONE_LABEL_TRANS_KEYS } from '../../../../constants';
import { FiltersType } from '../../types';

export type FilterMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  filters: FiltersType;
  evacZoneStyles: RegionEvacZoneStyle[];
  onCheckboxChange: (key: keyof FiltersType, subKey?: string) => void;
};

const checkboxSx = { mr: 1, p: 0 };

const getTranslationKeyForEvacZoneStatus = (
  evacZoneStyle: RegionEvacZoneStyle,
  status: string | EvacZoneStatus,
): string => {
  return status === 'all'
    ? 'geoEventEvacuations.evacZones.table.filter.all'
    : `${EVAC_ZONE_LABEL_TRANS_KEYS[evacZoneStyle][status as keyof (typeof EVAC_ZONE_LABEL_TRANS_KEYS)[typeof evacZoneStyle]]}Short`;
};

export const FilterMenu = ({
  anchorEl,
  open,
  onClose,
  filters,
  evacZoneStyles,
  onCheckboxChange,
}: FilterMenuProps): JSX.Element => {
  const { t } = useTranslation();

  const selectableStatuses = [
    'all',
    ...getEnabledEvacZoneStatuses(evacZoneStyles),
  ];

  // choose a style for text
  const evacZoneStyle = evacZoneStyles[0];

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiMenu-paper': { width: 200, height: 'auto', maxHeight: 'none' },
      }}
    >
      <MenuItem key="hasUpdates" onClick={() => onCheckboxChange('hasUpdates')}>
        <Checkbox
          checked={filters.hasUpdates}
          onChange={() => onCheckboxChange('hasUpdates')}
          onClick={(e) => e.stopPropagation()}
          sx={checkboxSx}
        />
        <Typography variant="body2">
          {t('geoEventEvacuations.evacZones.table.filter.hasUpdates')}
        </Typography>
      </MenuItem>
      <ListSubheader>
        {t('geoEventEvacuations.evacZones.table.filter.externalStatus')}
      </ListSubheader>
      {selectableStatuses.map((status) => (
        <MenuItem
          disabled={filters.hasUpdates}
          key={`external-${status}`}
          onClick={() =>
            !filters.hasUpdates && onCheckboxChange('external', status)
          }
        >
          <Checkbox
            checked={Boolean(filters.external[status])}
            onChange={() => onCheckboxChange('external', status)}
            onClick={(e) => e.stopPropagation()}
            sx={checkboxSx}
          />
          <Typography>
            {t(getTranslationKeyForEvacZoneStatus(evacZoneStyle, status))}
          </Typography>
        </MenuItem>
      ))}
      <ListSubheader>
        {t('geoEventEvacuations.evacZones.table.filter.internalStatus')}
      </ListSubheader>
      {selectableStatuses.map((status) => (
        <MenuItem
          disabled={filters.hasUpdates}
          key={`internal-${status}`}
          onClick={() =>
            !filters.hasUpdates && onCheckboxChange('internal', status)
          }
        >
          <Checkbox
            checked={Boolean(filters.internal[status])}
            onChange={() => onCheckboxChange('internal', status)}
            onClick={(e) => e.stopPropagation()}
            sx={checkboxSx}
          />
          <Typography>
            {t(getTranslationKeyForEvacZoneStatus(evacZoneStyle, status))}
          </Typography>
        </MenuItem>
      ))}
    </Menu>
  );
};
